import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const TYPE_LABELS = {
  suggestion: { label: 'Təklif',  color: '#10B981', bg: '#D1FAE5' },
  complaint:  { label: 'Şikayət', color: '#F59E0B', bg: '#FEF3C7' },
};

const STATUS_LABELS = {
  new:      { label: 'Yeni',        color: '#3B82F6', bg: '#DBEAFE' },
  read:     { label: 'Oxunub',      color: '#8B5CF6', bg: '#EDE9FE' },
  resolved: { label: 'Həll edildi', color: '#10B981', bg: '#D1FAE5' },
};

const STATUSES = ['new', 'read', 'resolved'];

export default function FeedbackPage() {
  const { getAdminToken } = useAuth();
  const [items, setItems]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected, setSelected]     = useState(null);
  const [updating, setUpdating]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let q = supabase
        .from('user_feedback')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterType !== 'all')   q = q.eq('type', filterType);
      if (filterStatus !== 'all') q = q.eq('status', filterStatus);

      const { data, error } = await q;
      if (error) { console.error('user_feedback fetch error:', error); }

      const rows = data || [];

      if (rows.length > 0) {
        const userIds = [...new Set(rows.map(r => r.user_id).filter(Boolean))];
        const { data: usersRes } = await supabase.rpc('admin_get_users_by_ids', { p_admin_token: getAdminToken(), p_ids: userIds });
        const usersData = usersRes?.success ? usersRes.rows : [];
        const usersMap = {};
        (usersData || []).forEach(u => { usersMap[u.id] = u; });
        setItems(rows.map(r => ({ ...r, users: usersMap[r.user_id] || null })));
      } else {
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setUpdating(true);
    await supabase.from('user_feedback').update({ status }).eq('id', id);
    setUpdating(false);
    if (selected?.id === id) setSelected(s => ({ ...s, status }));
    setItems(prev => prev.map(i => i.id === id ? { ...i, status } : i));
  };

  const openItem = async (item) => {
    setSelected(item);
    if (item.status === 'new') updateStatus(item.id, 'read');
  };

  const counts = {
    all:        items.length,
    suggestion: items.filter(i => i.type === 'suggestion').length,
    complaint:  items.filter(i => i.type === 'complaint').length,
    new:        items.filter(i => i.status === 'new').length,
  };

  return (
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1A1A2E', margin: 0 }}>
          💬 Təklif & Şikayətlər
        </h1>
        <p style={{ color: '#9898B0', marginTop: 4, fontSize: 14 }}>
          İstifadəçilərdən daxil olan mesajlar
        </p>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Cəmi',       value: counts.all,        color: '#6366F1', bg: '#EEF2FF' },
          { label: 'Təklif',     value: counts.suggestion, color: '#10B981', bg: '#D1FAE5' },
          { label: 'Şikayət',    value: counts.complaint,  color: '#F59E0B', bg: '#FEF3C7' },
          { label: 'Yeni / Oxunmamış', value: counts.new,  color: '#E63950', bg: '#FEE2E2' },
        ].map(s => (
          <div key={s.label} style={{
            background: s.bg, borderRadius: 12, padding: '14px 20px',
            display: 'flex', flexDirection: 'column', gap: 4, minWidth: 120,
          }}>
            <span style={{ fontSize: 24, fontWeight: 900, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'suggestion', 'complaint'].map(t => (
            <button key={t} onClick={() => setFilterType(t)} style={{
              padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              background: filterType === t ? '#E63950' : '#F1F5F9',
              color: filterType === t ? '#fff' : '#64748B',
            }}>
              {t === 'all' ? 'Hamısı' : TYPE_LABELS[t].label}
            </button>
          ))}
        </div>
        <div style={{ width: 1, height: 28, background: '#E2E8F0' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', ...STATUSES].map(st => (
            <button key={st} onClick={() => setFilterStatus(st)} style={{
              padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600,
              background: filterStatus === st ? '#6366F1' : '#F1F5F9',
              color: filterStatus === st ? '#fff' : '#64748B',
            }}>
              {st === 'all' ? 'Bütün statuslar' : STATUS_LABELS[st].label}
            </button>
          ))}
        </div>
        <button onClick={load} style={{
          marginLeft: 'auto', padding: '7px 14px', borderRadius: 8,
          border: '1px solid #E2E8F0', background: '#fff',
          cursor: 'pointer', fontSize: 13, color: '#64748B',
        }}>
          ↻ Yenilə
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16 }}>
        {/* List */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9898B0' }}>Yüklənir...</div>
          ) : items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, color: '#9898B0' }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
              <div>Mesaj yoxdur</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {items.map(item => {
                const tl = TYPE_LABELS[item.type] || {};
                const sl = STATUS_LABELS[item.status] || {};
                const isNew = item.status === 'new';
                const isActive = selected?.id === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => openItem(item)}
                    style={{
                      background: isActive ? '#F8F7FF' : '#fff',
                      border: `1.5px solid ${isActive ? '#6366F1' : isNew ? '#E63950' : '#E2E8F0'}`,
                      borderRadius: 12, padding: '14px 16px',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{
                        padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                        background: tl.bg, color: tl.color,
                      }}>{tl.label}</span>
                      <span style={{
                        padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        background: sl.bg, color: sl.color,
                      }}>{sl.label}</span>
                      {isNew && (
                        <span style={{
                          marginLeft: 'auto', width: 8, height: 8, borderRadius: 4, background: '#E63950',
                        }} />
                      )}
                    </div>
                    <p style={{
                      margin: 0, fontSize: 14, color: '#1A1A2E', lineHeight: 1.5,
                      display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      fontWeight: isNew ? 600 : 400,
                    }}>
                      {item.message}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                      <span style={{ fontSize: 12, color: '#9898B0' }}>
                        @{item.users?.username || '—'}
                      </span>
                      <span style={{ fontSize: 12, color: '#9898B0' }}>
                        {new Date(item.created_at).toLocaleDateString('az-AZ', {
                          day: '2-digit', month: 'short', year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && (
          <div style={{
            width: 340, flexShrink: 0,
            background: '#fff', borderRadius: 14,
            border: '1.5px solid #E2E8F0',
            padding: 20, alignSelf: 'flex-start',
            position: 'sticky', top: 20,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#1A1A2E' }}>Detallar</span>
              <button onClick={() => setSelected(null)} style={{
                border: 'none', background: '#F1F5F9', borderRadius: 8,
                width: 32, height: 32, cursor: 'pointer', fontSize: 18, color: '#64748B',
              }}>×</button>
            </div>

            {/* User info */}
            <div style={{
              background: '#F8F9FA', borderRadius: 10, padding: 12, marginBottom: 16,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1A1A2E' }}>
                {selected.users?.full_name || 'Ad yoxdur'}
              </div>
              <div style={{ fontSize: 12, color: '#9898B0', marginTop: 2 }}>
                @{selected.users?.username || '—'} · {selected.users?.email || '—'}
              </div>
            </div>

            {/* Badges */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
              <span style={{
                padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                background: TYPE_LABELS[selected.type]?.bg,
                color: TYPE_LABELS[selected.type]?.color,
              }}>
                {TYPE_LABELS[selected.type]?.label}
              </span>
              <span style={{ fontSize: 12, color: '#9898B0', marginLeft: 'auto', alignSelf: 'center' }}>
                {new Date(selected.created_at).toLocaleString('az-AZ')}
              </span>
            </div>

            {/* Message */}
            <div style={{
              background: '#F8F9FA', borderRadius: 10, padding: 14,
              fontSize: 14, color: '#1A1A2E', lineHeight: 1.6,
              marginBottom: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {selected.message}
            </div>

            {/* Status update */}
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#9898B0', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                Status
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {STATUSES.map(st => {
                  const sl = STATUS_LABELS[st];
                  const active = selected.status === st;
                  return (
                    <button
                      key={st}
                      onClick={() => !active && updateStatus(selected.id, st)}
                      disabled={active || updating}
                      style={{
                        padding: '9px 14px', borderRadius: 8,
                        border: active ? `2px solid ${sl.color}` : '1.5px solid #E2E8F0',
                        background: active ? sl.bg : '#fff',
                        color: active ? sl.color : '#64748B',
                        fontWeight: active ? 700 : 500,
                        cursor: active ? 'default' : 'pointer',
                        fontSize: 13, textAlign: 'left',
                        opacity: updating && !active ? 0.5 : 1,
                      }}
                    >
                      {active ? '✓ ' : ''}{sl.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
